import { getOptjobAsObject, OptJobType, useLatestOptJobStatus, useOptJobMutation } from 'api/optJobs/optJobs.api'
import { useSystem } from 'api/systems/systems.api'
import { getOptimizationFailureExplanationKey, optJobIsDone } from 'helpers/optimize.helper/optimize.helper'

import moment from 'moment'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import ButtonDropdown from 'ui/atoms/ButtonDropdown/ButtonDropdown'
import { useAuth } from '../AuthContext/AuthContext'
import { buttonTranslationMapping } from '../OptimizeFormProvider/components/OptimizeButton/OptimizeButton'
import styles from './OptimizeWithSubtypes.module.less'

type OptimizeWithSubtypesProps = {
  type: OptJobType
  disabled?: boolean
  optimizeAllSubtypes?: () => void
  isValid?: boolean
  optStartTime?: moment.Moment
  optEndTime?: moment.Moment
  creatingOptJob?: boolean
}

export default function OptimizeWithSubtypes({
  type,
  disabled,
  optimizeAllSubtypes,
  isValid,
  optStartTime,
  optEndTime,
  creatingOptJob,
}: OptimizeWithSubtypesProps): ReactElement {
  const { t } = useTranslation()
  const { systemId } = useAuth()
  const { data: activeSystem } = useSystem({ id: systemId })
  const { data: latestOptJob, status: fetchingLatestJobStatus } = useLatestOptJobStatus({ type })
  const firstLatestOptJob = getOptjobAsObject(latestOptJob)

  const { mutate: optJobCreate } = useOptJobMutation()
  const failureExplanationKey = getOptimizationFailureExplanationKey(firstLatestOptJob?.status)

  const subtypes =
    activeSystem?.subtypes[type]?.filter((subtype: Subtype) => subtype.enable_manually_trigger_opt_job !== false) ?? []

  function optimizeSubtypes(selectedSubtype: string): void {
    if (!activeSystem) return

    const startTime = optStartTime || moment().startOf('hour')
    optJobCreate({
      opt_job_type: type,
      input_data: {
        opt_start_time: startTime.toISOString(),
        opt_end_time: optEndTime?.startOf('second').toISOString(),
        include_subtypes: [selectedSubtype],
      },
    })
  }

  return (
    <>
      <div className={styles.OptimizeWithSubtypes_Button}>
        <ButtonDropdown
          variant="optimize"
          label={
            buttonTranslationMapping(
              firstLatestOptJob?.status,
              type === 'fuel_plan' ? t('Optimize scenario planning') : undefined
            ) ?? ''
          }
          tooltip={failureExplanationKey ? { title: t(failureExplanationKey) } : undefined}
          loading={creatingOptJob || fetchingLatestJobStatus === 'pending' || !optJobIsDone(firstLatestOptJob)}
          disabled={latestOptJob === undefined || !optJobIsDone(firstLatestOptJob) || !isValid || disabled}
          onButtonClick={() => {
            if (optimizeAllSubtypes) {
              optimizeAllSubtypes()
            }
          }}
          dropdownItems={subtypes.map((subtype: Subtype) => ({
            label: subtype.subtype_display_name,
            onClick: () => {
              optimizeSubtypes(subtype.subtype)
            },
          }))}
        />
      </div>
    </>
  )
}
